// src/components/DownloadModal.js
import React from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { TbCrown, TbDownload, TbX } from 'react-icons/tb';
import PricingPlans from './PricingPlans';

const DownloadModal = ({ 
  isOpen, 
  closeModal, 
  selectedTemplate, 
  onDownload, 
  freeTemplates = ['Professional', 'Classic', 'Dynamic', 'Modern']
}) => {
  // Check if selected template is in the free templates list
  const isTemplateAvailable = freeTemplates.includes(selectedTemplate);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={closeModal}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className={`w-full ${isTemplateAvailable ? 'max-w-md' : 'max-w-5xl'} transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all max-h-[95vh] flex flex-col my-2 sm:my-4`}>
                {isTemplateAvailable ? (
                  // Free Template Content
                  <>
                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-gray-900 flex items-center mb-6 p-6">
                      <TbDownload className="mr-2 text-blue-500" />
                      Download Your Document
                    </Dialog.Title>
                    <div className="px-6 pb-6">
                      <p className="text-gray-600 mb-6">
                        You're downloading the <span className="font-semibold">{selectedTemplate}</span> template.
                      </p>
                      
                      <div className="mt-6 flex space-x-3 justify-between">
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
                          onClick={closeModal}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="inline-flex justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none"
                          onClick={onDownload}
                        >
                          Download Now
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // Direct Plans inside Download Modal
                  <>
                    {/* Header Banner */}
                    <div className="bg-gradient-to-r from-violet-600 to-indigo-700 py-3.5 px-6 flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 bg-yellow-300 rounded-full flex items-center justify-center flex-shrink-0">
                          <TbCrown className="text-xl text-indigo-900" />
                        </div>
                        <div>
                          <h3 className="text-white text-lg md:text-xl font-bold">
                            Unlock Premium Download
                          </h3>
                          <p className="text-indigo-100 text-xs md:text-sm">
                            <span className="font-semibold text-yellow-200">{selectedTemplate}</span> is an ATS-optimized premium template. Choose a plan to download immediately.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        className="text-white/80 hover:text-white p-1.5 rounded-full hover:bg-white/10 transition-colors focus:outline-none"
                        onClick={closeModal}
                        aria-label="Close"
                      >
                        <TbX size={22} />
                      </button>
                    </div>
                    
                    {/* Direct Pricing Plans */}
                    <div className="p-3 sm:p-5 overflow-y-auto">
                      <PricingPlans isModal={true} />
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default DownloadModal;